import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Input from '@prism/input';
import PrismChip from './../chip/prismChip';
import PrismLabel from './../label/prismLabel';
import PrismRow from '../utilities/prismRow';
import PrismErrorsContainer from '../utilities/prismErrorsContainer';
import { FormGroup } from '@prism/form';

class PrismCodePicker extends React.PureComponent {
    getDefaultValue = (single) => single ? false : [];

    onCodeClick = (code) => {
        const {
            value,
            single,
            onChange
        } = this.props;

        const selection = value || this.getDefaultValue(single);

        let newValue;
        if (single) {
            newValue = code === selection ? '' : code;
        } else {
            if (value.includes(code)) {
                newValue = selection.length === 1
                    ? ''
                    : selection.filter((c) => c !== code);
            } else {
                newValue = selection.slice();
                newValue.push(code);
            }
        }

        onChange(newValue);
    }

    renderLabel = ({
        id,
        label,
        required
    }) => typeof label === 'string'
        ? (
            <PrismLabel for={id}>
                {label}
                {required && '*'}
            </PrismLabel>
        )
        : label;

    renderOptions({
        optionClassName,
        value,
        codes,
        enableSequence,
        single,
        disabled
    }) {
        if (codes.length === 0) {
            return false;
        }

        const className = classNames(
            'prism-codepicker__option',
            optionClassName
        );

        const selection = value  || this.getDefaultValue(single);
        const availableSelection = typeof codes[0] === 'string'
            ? codes.map((code) => ({ code: code, summary: code }))
            : codes;
        const sortedSelection = enableSequence
            ? _.sortBy(availableSelection, (el) => selection.indexOf(el.code) === -1 ? 999 : index)
            : availableSelection;

        const result = sortedSelection.map(({ code, summary }, key) => {
            const picked = single
                ? selection === code
                : selection.indexOf(code) !== -1;

            return (
                <a
                    className={className}
                    title={summary}
                    onClick={disabled ? null : () => this.onCodeClick(code)}
                    key={code + key}
                >
                    <PrismChip
                        selected={picked}
                        disabled={disabled}
                    >
                        {summary}
                    </PrismChip>
                </a>
            );
        });

        return result;
    }

    renderElement = ({
        contentClassName,
        bordered,
        errors
    }) => {
        const invalid = errors && errors.length > 0;

        const containerClassName = classNames(
            'prism-codepicker__content',
            { 'prism-codepicker__content_bordered': bordered },
            contentClassName
        );

        return (
            <>
                <div className={containerClassName}>
                    <PrismRow>
                        {this.renderOptions(this.props)}
                    </PrismRow>
                </div>
                <Input invalid={invalid} hidden />
            </>
        );
    }

    renderErrors = ({ errors }) => (
        <PrismErrorsContainer errors={errors} />
    )

    render() {
        const {
            className,
            label,
            errors,
            withFormGroup
        } = this.props;

        const containerClassName = classNames(
            'prism-sandbox',
            'prism-codepicker',
            className
        );

        const content = (
            <>
                {label && this.renderLabel(this.props)}
                {this.renderElement(this.props)}
                {errors && this.renderErrors(this.props)}
            </>
        );

        return (
            <div className={containerClassName}>
                {withFormGroup
                    ? <FormGroup>{content}</FormGroup>
                    : content}
            </div>
        );
    }
}

PrismCodePicker.propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    optionClassName: PropTypes.string,
    bordered: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                code: PropTypes.string,
                summary: PropTypes.string
            })
        ]))
    ]),
    codes: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            code: PropTypes.string,
            summary: PropTypes.string
        })
    ])).isRequired,
    enableSequence: PropTypes.bool,
    withFormGroup: PropTypes.bool,
    single: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired
};

PrismCodePicker.defaultProps = {
    bordered: true,
    value: [],
    codes: [],
    withFormGroup: true
};

export default PrismCodePicker;
